import { useCallback, useMemo } from "react"

import * as turf from "@turf/turf"

import { MapWithPhotoViewer } from "@l2r-front/l2r-common"
import { useImageCollectionStateContext, useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import { ReactMapGl, useMapContext } from "@l2r-front/l2r-map"
import { RoadSearch, useIsFetchingNetwork, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useIsFetchingEquipment } from "../../../modules/equipment/hooks/useIsFetchingEquipment"
import { useIsFetchingHorizontalSigning } from "../../../modules/horizontalSigning/hooks/useIsFetchingHorizontalSigning"
import { useIsFetchingVerticalSigning } from "../../../modules/verticalSigning/hooks/useIsFetchingVerticalSigning"

import { CityMapLegend } from "../CityMapLegend/CityMapLegend"

export function Map(props) {

    const {
        children,
        mapProps,
        ...otherProps
    } = props

    const isFetchingEquipment = useIsFetchingEquipment()
    const isFetchingHorizontalSigning = useIsFetchingHorizontalSigning()
    const isFetchingVerticalSigning = useIsFetchingVerticalSigning()
    const isFetchingNetwork = useIsFetchingNetwork()
    const isFetching = isFetchingEquipment || isFetchingHorizontalSigning || isFetchingVerticalSigning || isFetchingNetwork
    const { selectedNetwork } = useNetworksStateContext()
    const [mapState, mapDispatch] = useMapContext()
    const { imageLayerDisplayed } = useImageCollectionStateContext()
    const { onOpenImageViewer, onSwitchImageLayer, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const { error } = mapState
    const { setMapBoundingBoxToFit } = mapDispatch
    const isMobile = useIsMobileDevice()

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [selectedNetwork])

    const onRecenterOnNetworkBoundingBox = useCallback(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        setMapBoundingBoxToFit({
            minLng,
            minLat,
            maxLng,
            maxLat,
        })
    }, [selectedNetwork, setMapBoundingBoxToFit])

    const onSwitchImageLayerCallback = isImageCollectionAvailable() ? onSwitchImageLayer : undefined

    return (
        <MapWithPhotoViewer
            dragRotate={false}
            error={error}
            imageLayerDisplayed={imageLayerDisplayed}
            initialViewState={mapInitialViewState}
            isLoading={isFetching}
            onOpenImageViewer={onOpenImageViewer}
            onSwitchImageLayer={onSwitchImageLayerCallback}
            onRecenter={onRecenterOnNetworkBoundingBox}
            topLeftElements={[<RoadSearch key="road-search" disableExpend={!isMobile} />, <CityMapLegend key="city-map-legend" />]}
            {...mapProps}
            {...otherProps}
        >
            {children}
        </MapWithPhotoViewer>
    )
}

Map.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
}